import type { UserRole } from '@ofmos/shared/auth';
import { useAtom } from 'jotai';
import { type ReactNode, useId } from 'react';
import { useNavigate } from 'react-router';
import { useObjects } from '../../data/hooks';
import {
  Avatar,
  cx,
  DropdownItem,
  DropdownMenu,
  DropdownRadioGroup,
  DropdownRadioItem,
  DropdownSeparator,
  DropdownSub,
  Icon,
} from '../../ui';
import { useLogout, useSession } from '../auth/useSession';
import { objectByName } from '../dashboard/queries';
import { SETTINGS_PANES } from '../nav/settingsStack';
import {
  DEFAULT_SIDEBAR_ICONS,
  normaliseSidebarIcons,
  normaliseSidebarSize,
  normaliseSymbolStyle,
  normaliseSymbolStyleMode,
  normaliseSymbolTint,
  normaliseThemePreference,
  type SidebarIcons,
  type SidebarSize,
  SYMBOL_STYLE_MODES,
  SYMBOL_TINTS,
  type SymbolStyle,
  type SymbolStyleMode,
  type SymbolTint,
  sidebarIconsAtom,
  sidebarSizeAtom,
  symbolStyleAtom,
  symbolStyleModeAtom,
  symbolTintAtom,
  type ThemePreference,
  themeAtom,
  useSetAppearance,
  useSetSidebarIcons,
  useSetSidebarSize,
  useSetSymbolStyle,
  useSetSymbolStyleMode,
  useSetSymbolTint,
} from '../session';
import {
  ICON_OPTIONS,
  MODE_LABELS,
  SIZE_OPTIONS,
  STYLE_OPTIONS,
  styleModeHeading,
  THEME_OPTIONS,
  TINT_FILLS,
  TINT_LABELS,
} from './appearanceOptions';

/**
 * THE ACCOUNT BUTTON AND ITS MENU (redesign register R-S3: "Account menu as in the mockup
 * (Settings, Team members, Welcome messages, Light/Dark/Automatic, Sign out); menu section labels
 * in sentence case"; L15: "the account menu keeps Light, Dark, Automatic (Automatic default)").
 * The look is `dashboard-v2-side.html:101-118`: the person at the foot of the sidebar, the menu
 * opening over it.
 *
 * TWO CHANGES OF 2026-10-05 (BR-SETTINGS). The Welcome messages row opens the bot's pane and
 * carries its configured title, because the welcome messages are that pane's first section now
 * and one thing has one destination (register A8-3). And the Appearance section holds the sidebar
 * settings the Appearance pane held, under Light, Dark and Automatic (A8-7), each as Apple builds
 * a setting with several values in a menu: a submenu whose items carry a checkmark on the one in
 * effect (hig: Menus, "Submenus" and "Toggled items"; kit: Menus/Light/Regular/Menu with
 * Selection; `DropdownSub`), never a pop-up button inside the menu.
 *
 * WHAT LEFT THE SIDEBAR FOR IT: the Settings row, the Appearance menu row, the Sign out row and
 * the name-and-address block. The address is the menu's label now, which is still what tells
 * somebody WHICH account this window is signed into.
 */

/**
 * THE ROLE LINE UNDER THE NAME is the session's display role in its label form (coordinator
 * decision Q-NAV-4): `AuthenticatedUser.role`, which is "Display and nav only"
 * (`packages/shared/src/auth.ts`).
 */
export function roleLine(role: UserRole | undefined): string | null {
  if (role === undefined) return null;
  return `${role.charAt(0).toUpperCase()}${role.slice(1)}`;
}

/**
 * A MENU LABEL IN SENTENCE CASE WITH NORMAL TRACKING (R-S3), for this menu only: the product's
 * `DropdownLabel` sets spaced capitals, and the other menus change in the next round (A6), so the
 * account menu draws its two labels itself with the same box, size, weight and ink.
 */
function MenuLabel({ id, children }: { readonly id?: string; readonly children: ReactNode }) {
  return (
    <div id={id} className="truncate px-2.5 py-2 text-2xs font-semibold text-ink-subtle">
      {children}
    </div>
  );
}

/**
 * ONE TEXT COLUMN FOR THE WHOLE MENU, as the mockup draws it (`dashboard-v2-side.html:102-117`,
 * a tick column on every item): a radio item carries the 16px tick gutter, so the plain items
 * carry the same empty gutter and every word starts at one x.
 */
function Gutter() {
  return <span aria-hidden="true" className="w-4 shrink-0" />;
}

export function AccountMenu() {
  const session = useSession();
  const person = session.data;
  const logout = useLogout();
  const navigate = useNavigate();
  const objects = useObjects();
  /**
   * "Team members" opens the Team members LIST, the object list of the Team group, not the
   * log-ins pane (coordinator decision Q-NAV-5: "A word must not lie"); it is there where the
   * object is, and its words are the object's own plural label.
   */
  const team = objectByName(objects.data, 'team_member');
  /** The bot's pane shows where the welcome messages' object does (fulfillment P2 item 5). */
  const templates = objectByName(objects.data, 'message_template');
  const [storedTheme] = useAtom(themeAtom);
  const theme = normaliseThemePreference(storedTheme) ?? 'automatic';
  const setAppearance = useSetAppearance();
  const appearanceId = useId();
  const sidebar = useSidebarAppearance();
  const name = person?.name ?? 'Signed out';
  const role = roleLine(person?.role);

  return (
    <DropdownMenu
      align="start"
      trigger={
        <button
          type="button"
          /* The sidebar row's own grounds: its radius, and the unemphasized selection fill
             under the pointer and while the menu is open (`.shell-nav-row`, index.css). */
          className="shell-account w-full rounded-(--radius-nav-item) text-(--color-shell-ink) hover:bg-(--color-selection-unemphasized) data-[state=open]:bg-(--color-selection-unemphasized)"
        >
          <Avatar name={name} size="sm" />
          <span className="min-w-0 flex-1 text-left">
            <span className="block truncate text-sm font-semibold">{name}</span>
            {role !== null && (
              <span className="block truncate text-2xs text-ink-muted">{role}</span>
            )}
          </span>
          {/* The up and down chevron pair of the product's pop-up button
              (`ui/SettingsGroup/SettingsGroup.tsx`, `SettingsPopUpBody`): this opens a menu. */}
          <span aria-hidden="true" className="flex shrink-0 flex-col items-center text-ink-muted">
            <Icon name="chevron-up" size="0.5rem" />
            <Icon name="chevron-down" size="0.5rem" />
          </span>
        </button>
      }
    >
      {person?.email !== undefined && <MenuLabel>{person.email}</MenuLabel>}
      {/* "Settings…" keeps the ellipsis as drawn (coordinator decision Q-NAV-11). */}
      <DropdownItem onSelect={() => void navigate('/settings')}>
        <Gutter />
        Settings…
      </DropdownItem>
      {team !== undefined && (
        <DropdownItem onSelect={() => void navigate(`/objects/${team.id}`)}>
          <Gutter />
          {team.labelPlural}
        </DropdownItem>
      )}
      {templates !== undefined && (
        <DropdownItem onSelect={() => void navigate('/settings/bot')}>
          <Gutter />
          {SETTINGS_PANES.bot}
        </DropdownItem>
      )}
      <DropdownSeparator />
      {/* The label names the group for a screen reader, as `DropdownRadioGroup`'s own does. */}
      <fieldset aria-labelledby={appearanceId} className="min-w-0">
        <MenuLabel id={appearanceId}>Appearance</MenuLabel>
        <DropdownRadioGroup
          value={theme}
          onValueChange={(value) => setAppearance(value as ThemePreference)}
        >
          {THEME_OPTIONS.map((option) => (
            <DropdownRadioItem key={option.value} value={option.value}>
              {option.label}
            </DropdownRadioItem>
          ))}
        </DropdownRadioGroup>
        <SidebarAppearanceSubmenus {...sidebar} />
      </fieldset>
      <DropdownSeparator />
      <DropdownItem
        disabled={logout.isPending}
        onSelect={() =>
          logout.mutate(undefined, {
            onSettled: () => void navigate('/login', { replace: true }),
          })
        }
      >
        <Gutter />
        Sign out
      </DropdownItem>
    </DropdownMenu>
  );
}

/**
 * THE SIDEBAR SETTINGS THE APPEARANCE PANE HELD, read and written exactly as the pane did: the same
 * atoms, the same per-person preference setters, the same fallbacks (Symbols by default, register
 * A5-2; Large, the size `useApplyTheme` draws with), so a choice made on the pane reads here
 * unchanged.
 */
function useSidebarAppearance() {
  const [storedIcons] = useAtom(sidebarIconsAtom);
  const [storedStyle] = useAtom(symbolStyleAtom);
  const [storedMode] = useAtom(symbolStyleModeAtom);
  const [storedTint] = useAtom(symbolTintAtom);
  const [storedSize] = useAtom(sidebarSizeAtom);
  const style = normaliseSymbolStyle(storedStyle) ?? 'default';
  const mode = normaliseSymbolStyleMode(storedMode) ?? 'auto';
  const modes = SYMBOL_STYLE_MODES[style];
  return {
    icons: normaliseSidebarIcons(storedIcons) ?? DEFAULT_SIDEBAR_ICONS,
    style,
    modes,
    /** A mode the current style does not offer reads as Auto, which is what the resolver does. */
    mode: modes.includes(mode) ? mode : 'auto',
    tint: normaliseSymbolTint(storedTint) ?? 'multicolour',
    size: normaliseSidebarSize(storedSize) ?? 'large',
    setIcons: useSetSidebarIcons(),
    setStyle: useSetSymbolStyle(),
    setMode: useSetSymbolStyleMode(),
    setTint: useSetSymbolTint(),
    setSize: useSetSidebarSize(),
  };
}

/**
 * ONE SUBMENU PER SETTING, IN THE PANE'S ORDER, SHOWN WHEN THE PANE SHOWED IT. "Sidebar icons"
 * and "Sidebar icon size" always; "Icon style", the style's own modes and "Theme colour" only
 * while the sidebar draws tiles (and the modes only for a style that has them, the colour only
 * for Tinted), because they act on nothing else: Apple hides a menu item that does not apply
 * rather than dimming it (DESIGN.md B18). Hiding is not clearing, so a round trip through
 * Symbols gives a person their own tiles back. The words are the pane's (`appearanceOptions.ts`).
 *
 * Each submenu's items are one radio group with no heading of its own: the submenu's title names
 * it (`DropdownSub` labels its panel by its row), and the checkmark marks the value in effect.
 */
function SidebarAppearanceSubmenus({
  icons,
  style,
  modes,
  mode,
  tint,
  size,
  setIcons,
  setStyle,
  setMode,
  setTint,
  setSize,
}: ReturnType<typeof useSidebarAppearance>) {
  const tiles = icons === 'tiles';
  return (
    <>
      <DropdownSub label="Sidebar icons">
        <DropdownRadioGroup
          value={icons}
          onValueChange={(value) => setIcons(value as SidebarIcons)}
        >
          {ICON_OPTIONS.map((option) => (
            <DropdownRadioItem key={option.value} value={option.value}>
              {option.label}
            </DropdownRadioItem>
          ))}
        </DropdownRadioGroup>
      </DropdownSub>
      {tiles && (
        <DropdownSub label="Icon style">
          <DropdownRadioGroup
            value={style}
            onValueChange={(value) => setStyle(value as SymbolStyle)}
          >
            {STYLE_OPTIONS.map((option) => (
              <DropdownRadioItem key={option.value} value={option.value}>
                {option.label}
              </DropdownRadioItem>
            ))}
          </DropdownRadioGroup>
        </DropdownSub>
      )}
      {tiles && modes.length > 0 && (
        <DropdownSub label={styleModeHeading(style)}>
          <DropdownRadioGroup
            value={mode}
            onValueChange={(value) => setMode(value as SymbolStyleMode)}
          >
            {modes.map((value) => (
              <DropdownRadioItem key={value} value={value}>
                {MODE_LABELS[value]}
              </DropdownRadioItem>
            ))}
          </DropdownRadioGroup>
        </DropdownSub>
      )}
      {tiles && style === 'tinted' && (
        <DropdownSub label="Theme colour">
          <DropdownRadioGroup value={tint} onValueChange={(value) => setTint(value as SymbolTint)}>
            {SYMBOL_TINTS.map((value) => (
              <DropdownRadioItem key={value} value={value}>
                <span aria-hidden="true" className={cx('tint-swatch', TINT_FILLS[value])} />
                {TINT_LABELS[value]}
              </DropdownRadioItem>
            ))}
          </DropdownRadioGroup>
        </DropdownSub>
      )}
      <DropdownSub label="Sidebar icon size">
        <DropdownRadioGroup value={size} onValueChange={(value) => setSize(value as SidebarSize)}>
          {SIZE_OPTIONS.map((option) => (
            <DropdownRadioItem key={option.value} value={option.value}>
              {option.label}
            </DropdownRadioItem>
          ))}
        </DropdownRadioGroup>
      </DropdownSub>
    </>
  );
}
